// Classic footer. A view over core's useSiteFooter() (§4.5): the
// section-gated links, footer text, the "Powered by ScoreLM" attribution rule
// and the Admin link live in core/chrome/useSiteFooter; this keeps today's markup.

import { Link } from 'react-router-dom'
import { useSiteFooter } from '@/site/core/chrome/useSiteFooter'
import { sf } from '@/site/core/features/sf'
import { useT } from '@/i18n'
import { siteClassicMessages } from '@/i18n/catalogs/site-classic'
import { LanguageSelect } from '@/site/kit/i18n/LanguageSelect'
import { CLASSIC_SELECT_CHEVRON } from './selectChevron'

export default function LeagueFooter() {
  const footer = useSiteFooter()
  const c = useT(siteClassicMessages).chrome
  const currentYear = footer.year

  if (footer.leagueName === null) {
    return (
      <footer className="relative border-t border-secondary/20 bg-bg-primary" {...sf('site.footer')}>
        <div className="container mx-auto px-4 py-12">
          <div className="flex items-center justify-center">
            <span className="text-gray-400 text-sm">© {currentYear}</span>
          </div>
        </div>
      </footer>
    )
  }

  const tenantName = footer.leagueName
  const footerText = footer.footerText
  const showCredit = footer.poweredBy

  // Footer links follow the tenant's site sections, like the main nav.
  const footerLinks = footer.links

  return (
    <footer className="relative border-t border-secondary/20 bg-bg-primary" {...sf('site.footer')}>
      <div className="container mx-auto px-4 py-12">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center space-x-3">
            {footer.logoUrl ? (
              <img
                src={footer.logoUrl}
                alt={tenantName}
                className="w-8 h-8 object-contain"
              />
            ) : null}
            <div className="flex flex-col">
              <span className="text-gray-400 text-sm">
                © {currentYear} {tenantName}
              </span>
              {footerText && (
                <span className="text-gray-400 text-xs mt-1">
                  {footerText}
                </span>
              )}
              {showCredit && (
                <a
                  href={footer.poweredByHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-primary text-xs mt-1 transition-colors"
                  {...sf('site.footer.powered-by')}
                >
                  {c.poweredBy}
                </a>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
            {footerLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="text-gray-400 hover:text-primary transition-colors"
              >
                {link.label}
              </Link>
            ))}
            <Link
              to={footer.adminHref}
              className="text-gray-400 hover:text-primary transition-colors"
            >
              {c.admin}
            </Link>
            <LanguageSelect
              model={footer.language}
              className="flex items-center gap-2 text-gray-400"
              selectClassName="appearance-none bg-bg-primary text-gray-300 border border-white/20 rounded pl-2 pr-9 py-1 text-base md:text-sm [@media(pointer:coarse)]:min-h-[44px]"
              selectStyle={CLASSIC_SELECT_CHEVRON}
            />
          </div>
        </div>
      </div>
    </footer>
  )
}
